import React, { useEffect, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Plus, Pencil, Trash2, X, Zap } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", message: "" };

export default function WaShortcuts() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/wa/shortcuts");
      setList(data || []);
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditingId(null); setModal(true); };
  const openEdit = (s) => { setForm({ name: s.name, message: s.message }); setEditingId(s.id); setModal(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/wa/shortcuts/${editingId}`, form);
        toast.success("Shortcut diperbarui");
      } else {
        await api.post("/wa/shortcuts", form);
        toast.success("Shortcut dibuat");
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan shortcut");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/wa/shortcuts/${confirmDel.id}`);
      toast.success("Shortcut dihapus");
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="label-cap mb-2">WhatsApp</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black tracking-tight">Shortcut Pesan</h1>
          <p className="text-xs text-[#52525B] mt-2">Template balasan cepat untuk inbox WhatsApp. Gunakan <span className="font-mono font-bold">{"{nama}"}</span> agar otomatis diganti nama kontak saat dipakai.</p>
        </div>
        <button onClick={openCreate} data-testid="add-shortcut-button" className="bg-[#16A34A] hover:bg-[#15803D] text-white text-xs font-bold uppercase tracking-wider px-4 py-3 flex items-center justify-center gap-2 min-h-[44px]">
          <Plus size={14} /> Shortcut Baru
        </button>
      </div>

      <div className="bg-white border border-[#E4E4E7]">
        {loading ? (
          <div className="p-8 text-center text-sm text-[#52525B]">Memuat…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#52525B]" data-testid="shortcut-empty">
            Belum ada shortcut. Klik "Shortcut Baru" untuk membuat template pesan pertama.
          </div>
        ) : (
          <div className="divide-y divide-[#E4E4E7]">
            {list.map((s) => (
              <div key={s.id} className="px-4 py-3 flex items-start gap-3" data-testid={`shortcut-row-${s.id}`}>
                <div className="mt-0.5 text-[#16A34A]"><Zap size={16} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm" data-testid={`shortcut-name-${s.id}`}>/{s.name}</div>
                  <div className="text-xs text-[#52525B] whitespace-pre-wrap break-words mt-0.5 line-clamp-3">{s.message}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(s)} data-testid={`shortcut-edit-${s.id}`} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><Pencil size={14} /></button>
                  <button onClick={() => setConfirmDel(s)} data-testid={`shortcut-delete-${s.id}`} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[#E4E4E7] max-w-lg w-full" data-testid="shortcut-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">{editingId ? "Ubah" : "Tambah"} Shortcut</h2>
              <button type="button" onClick={() => setModal(false)} className="p-2 hover:bg-[#F4F4F5]"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="label-cap block mb-1.5">Nama Shortcut<span className="text-[#DC2626]">*</span></label>
                <input
                  data-testid="shortcut-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="mis. salam, garansi, harga-survey"
                  className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]"
                />
              </div>
              <div>
                <label className="label-cap block mb-1.5">Isi Pesan<span className="text-[#DC2626]">*</span></label>
                <textarea
                  data-testid="shortcut-message-input"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                  rows={6}
                  placeholder={"Halo {nama}, terima kasih sudah menghubungi kami…"}
                  className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]"
                />
                <p className="text-[10px] text-[#52525B] mt-1"><span className="font-mono font-bold">{"{nama}"}</span> akan diganti nama kontak otomatis saat dipakai di inbox.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" disabled={saving} data-testid="shortcut-submit-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#16A34A] text-white hover:bg-[#15803D] disabled:opacity-50">Simpan</button>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">Hapus Shortcut?</h3>
            <p className="text-sm text-[#52525B] mb-6">"/{confirmDel.name}" akan dihapus permanen.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} data-testid="confirm-delete-shortcut-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
